import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { faltaParaEmailTt, type DadosTt, type EnderecoTt } from './ttFixaDados';

/* O E-MAIL DA TT FIXA COM O CONTRATO PF E O ENDEREÇO (Douglas 24/10/2026,
   maquete aprovada): o e-mail à gerente de contas precisa dos dois, e quem
   destrava a TT é a BKO. Faltando, ela preenche ali mesmo; o que preencher vai
   para os mesmos campos do item no Pedido 360 (itens_pedido.tt_contrato_pf e
   endereco_instalacao). Completo = a régua do e-mail (tt-fixa-gerente.service):
   CEP, logradouro, número, cidade e UF. */

const enderecoTexto = (e: EnderecoTt) => [
  [e.logradouro, e.numero].filter(Boolean).join(', '),
  e.complemento, e.bairro, [e.cidade, e.uf].filter(Boolean).join('/'), e.cep,
].filter(Boolean).join(' · ');

export function TtEmailPopover({ aberto, onAberto, titulo, descricao, proximo, dados, salvando, enviando, onEnviar, children }: {
  aberto: boolean; onAberto: (o: boolean) => void; titulo: string; descricao: string; proximo: number;
  dados: DadosTt | undefined; salvando: boolean; enviando: boolean;
  onEnviar: (novos: DadosTt | null) => void; children: ReactNode;
}) {
  const [contratoPf, setContratoPf] = useState('');
  const [endereco, setEndereco] = useState<EnderecoTt>({});
  const [editando, setEditando] = useState(false);
  const falta = faltaParaEmailTt(dados);

  useEffect(() => {
    if (!aberto) return;
    setContratoPf(dados?.contratoPf ?? '');
    setEndereco(dados?.endereco ?? {});
    setEditando(faltaParaEmailTt(dados).length > 0);
  }, [aberto, dados]);

  const buscarCep = async (cep: string) => {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const d = await r.json();
      if (!d.erro) setEndereco((e) => ({ ...e, cep: limpo, logradouro: d.logradouro || e.logradouro, bairro: d.bairro || e.bairro, cidade: d.localidade || e.cidade, uf: d.uf || e.uf }));
    } catch { /* sem a busca, ela digita */ }
  };

  const novos: DadosTt = { contratoPf: contratoPf.trim(), endereco };
  const faltaAgora = faltaParaEmailTt(novos);
  const mudou = contratoPf.trim() !== (dados?.contratoPf ?? '').trim() || JSON.stringify(endereco) !== JSON.stringify(dados?.endereco ?? {});
  const ocupado = salvando || enviando;
  const campo = (rotulo: string, chave: keyof EnderecoTt, placeholder: string, obrigatorio = true, extra?: (v: string) => void) => (
    <label className="grid gap-0.5">
      <span className="text-[10.5px] text-muted-foreground">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <Input value={endereco[chave] ?? ''} placeholder={placeholder} disabled={ocupado}
        className={cn('h-8 text-xs', obrigatorio && !endereco[chave]?.trim() && 'border-danger/60')}
        onChange={(e) => { const v = chave === 'uf' ? e.target.value.toUpperCase() : e.target.value; setEndereco((x) => ({ ...x, [chave]: v })); extra?.(v); }} />
    </label>
  );

  return (
    <Popover open={aberto} onOpenChange={onAberto}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[380px] p-3" align="end">
        <p className="mb-1 text-xs font-semibold">{titulo}</p>
        <p className="mb-2 text-[11px] text-muted-foreground">{descricao}</p>
        {editando ? (
          <div className="space-y-2">
            {falta.length > 0 && (
              <p className="text-[11px] text-muted-foreground">Para sair, o e-mail precisa do Contrato PF da fibra e do endereço completo. O que você preencher aqui fica salvo no pedido.</p>
            )}
            <p className="pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Contrato PF da fibra</p>
            <Input value={contratoPf} disabled={ocupado} placeholder="Número do contrato da fibra no nome da pessoa física"
              className={cn('h-8 text-xs', !contratoPf.trim() && 'border-danger/60')} onChange={(e) => setContratoPf(e.target.value)} />
            {!contratoPf.trim() && <p className="text-[10.5px] text-danger">Falta. Está na conta da fibra do cliente pessoa física.</p>}
            <p className="pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Endereço da instalação</p>
            <div className="grid grid-cols-[1fr_72px] gap-2">
              {campo('CEP', 'cep', '00000-000', true, (v) => void buscarCep(v))}
              {campo('UF', 'uf', 'RS')}
            </div>
            {campo('Logradouro', 'logradouro', 'Rua, avenida')}
            <div className="grid grid-cols-[1fr_72px_1fr] gap-2">
              {campo('Bairro', 'bairro', 'Bairro', false)}
              {campo('Número', 'numero', '123')}
              {campo('Complemento', 'complemento', 'Apto, sala', false)}
            </div>
            {campo('Cidade', 'cidade', 'Cidade')}
            <Button size="sm" className="h-8 w-full" disabled={ocupado || faltaAgora.length > 0} onClick={() => onEnviar(mudou ? novos : null)}>
              {ocupado ? <Loader2 className="h-3 w-3 animate-spin" />
                : faltaAgora.length > 0 ? `Salvar e enviar o ${proximo}º e-mail · falta ${faltaAgora.join(' e ')}`
                  : mudou ? `Salvar e enviar o ${proximo}º e-mail` : `Enviar o ${proximo}º e-mail`}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="rounded-md border border-border px-2.5 py-2 text-[11px] leading-relaxed">
              <button type="button" className="float-right text-[10.5px] font-semibold text-primary hover:underline" disabled={ocupado} onClick={() => setEditando(true)}>editar</button>
              <span className="font-semibold">Contrato PF</span> {dados?.contratoPf}<br />
              <span className="font-semibold">Endereço</span> {enderecoTexto(dados?.endereco ?? {})}
            </div>
            <Button size="sm" className="h-8 w-full" disabled={ocupado} onClick={() => onEnviar(null)}>
              {ocupado ? <Loader2 className="h-3 w-3 animate-spin" /> : `Enviar o ${proximo}º e-mail`}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
